<script setup>
import { computed } from "vue";
import { useAuthUser } from "@/composables/useAuthUser.js";
import BrandLogo from "@/Components/Brand/BrandLogo.vue";

const authUser = useAuthUser();

const leftLinks = computed(() => [
    authUser.value
        ? { label: "My Gift Hero", route: "dashboard" }
        : { label: "Log In", route: "login" },
    { label: "Gift Ideas", route: "explore" },
    { label: "Browser Extension", route: "browser-extensions" },
    { label: "Mobile App", route: "mobile" },
]);

const rightLinks = [
    { label: "About Gift Hero", route: "about-us" },
    { label: "Support", route: "help" },
];

// Not in the Figma footer; placed under the copyright as on gifthero.com.
const legalLinks = [
    { label: "Privacy Policy", route: "policy.show" },
    { label: "Terms of Use", route: "terms.show" },
    { label: "Contact", route: "contact-us" },
];

const socialLinks = [
    {
        label: "Gift Hero on Instagram",
        href: "https://www.instagram.com/gift_hero",
        icon: "images/brand/social-1.svg",
    },
    {
        label: "Gift Hero on Facebook",
        href: "https://www.facebook.com/GiftHero",
        icon: "images/brand/social-2.svg",
    },
];
</script>

<template>
    <footer class="px-5 pb-5 max-lg:p-0">
        <div
            class="relative mx-auto max-w-[1472px] overflow-hidden rounded-b-brand bg-[#102A3D] px-8 py-12 text-white max-lg:rounded-none sm:rounded-b-[10px] md:px-16"
        >
            <!--
                Ghost bug: flat Figma export (node "image 28", tones baked in)
                multiplied over the navy so the circle darkens the background
                and the mark stays at background tone.
            -->
            <img
                :src="asset('images/brand/footer-ghost.webp')"
                alt=""
                aria-hidden="true"
                class="pointer-events-none absolute left-1/2 top-0 h-full w-auto max-w-none -translate-x-1/2 mix-blend-multiply"
            />

            <!--
                Desktop (md+): three columns — links + social, centred logo,
                links + copyright. Base-display + max-md:hidden (not bare
                `hidden md:grid`): the legacy .hidden{display:none!important}
                rule on app.scss pages would keep this hidden at every width.
            -->
            <div
                class="relative grid gap-10 max-md:hidden md:min-h-[200px] md:grid-cols-3"
            >
                <!-- Left: nav links over social icons -->
                <div class="flex flex-col justify-between gap-10">
                    <ul class="flex flex-col gap-2 font-brand text-base">
                        <li v-for="link in leftLinks" :key="link.label">
                            <!--
                                Explicit color: legacy `a { color: … }` beats
                                inheritance on pages that load app.scss.
                            -->
                            <a
                                :href="route(link.route)"
                                class="text-white hover:text-white/70"
                            >
                                {{ link.label }}
                            </a>
                        </li>
                    </ul>
                    <div class="flex gap-3">
                        <a
                            v-for="social in socialLinks"
                            :key="social.label"
                            :href="social.href"
                            target="_blank"
                            rel="noopener noreferrer"
                            :aria-label="social.label"
                            class="transition hover:opacity-80"
                        >
                            <img
                                :src="asset(social.icon)"
                                alt=""
                                class="size-8"
                            />
                        </a>
                    </div>
                </div>

                <!-- Center: logo, vertically centered in the footer -->
                <div class="flex items-center justify-center">
                    <BrandLogo variant="inverse" />
                </div>

                <!-- Right: nav links over copyright -->
                <div
                    class="flex flex-col justify-between gap-10 md:items-end md:text-right"
                >
                    <ul
                        class="flex flex-col gap-2 font-brand text-base md:items-end"
                    >
                        <li v-for="link in rightLinks" :key="link.label">
                            <a
                                :href="route(link.route)"
                                class="text-white hover:text-white/70"
                            >
                                {{ link.label }}
                            </a>
                        </li>
                    </ul>
                    <div class="flex flex-col gap-3 md:items-end">
                        <p
                            class="whitespace-nowrap font-brand text-[15px] text-white/50"
                        >
                            © 2026 Gift Hero Inc. All Rights Reserved
                        </p>
                        <ul
                            class="flex flex-wrap gap-x-4 gap-y-1 font-brand text-[13px] md:justify-end"
                        >
                            <li v-for="link in legalLinks" :key="link.label">
                                <a
                                    :href="route(link.route)"
                                    class="text-white/70 hover:text-white"
                                >
                                    {{ link.label }}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!--
                Mobile: two link columns side by side, then the logo, the social
                icons, and the copyright stacked beneath.
            -->
            <div class="relative md:hidden">
                <div class="grid grid-cols-2 gap-x-6 gap-y-3">
                    <ul class="flex flex-col gap-3 font-brand text-base">
                        <li v-for="link in leftLinks" :key="link.label">
                            <a
                                :href="route(link.route)"
                                class="text-white hover:text-white/70"
                            >
                                {{ link.label }}
                            </a>
                        </li>
                    </ul>
                    <ul
                        class="flex flex-col items-end gap-3 text-right font-brand text-base"
                    >
                        <li v-for="link in rightLinks" :key="link.label">
                            <a
                                :href="route(link.route)"
                                class="text-white hover:text-white/70"
                            >
                                {{ link.label }}
                            </a>
                        </li>
                    </ul>
                </div>

                <div class="mt-10 flex justify-center">
                    <BrandLogo variant="inverse" />
                </div>

                <div class="mt-8 flex justify-center gap-3">
                    <a
                        v-for="social in socialLinks"
                        :key="social.label"
                        :href="social.href"
                        target="_blank"
                        rel="noopener noreferrer"
                        :aria-label="social.label"
                        class="transition hover:opacity-80"
                    >
                        <img :src="asset(social.icon)" alt="" class="size-8" />
                    </a>
                </div>

                <p
                    class="mt-8 text-center font-brand text-[15px] text-white/50"
                >
                    © 2026 Gift Hero Inc. All Rights Reserved
                </p>
                <ul
                    class="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1 font-brand text-[13px]"
                >
                    <li v-for="link in legalLinks" :key="link.label">
                        <a
                            :href="route(link.route)"
                            class="text-white/70 hover:text-white"
                        >
                            {{ link.label }}
                        </a>
                    </li>
                </ul>
            </div>
        </div>
    </footer>
</template>
